import { clsx } from '@alexis/helpers/clsx';
import { translateText } from '@alexis/helpers/translation';
import { throttle } from 'lodash-es';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { getLocaleState, getTranslationsState } from '@redux/selectors';

import { Translations } from '@wegoTypes/translations';

import styles from './HotelAnchorTabs.module.scss';

interface ITabItem {
  name: string;
  link: string;
}

export const STICKY_BAR_HEIGHT = 20;
const ROOMS_DEAL_MARGIN_TOP = 48;

const HotelAnchorTabs: React.FC = () => {
  const locale: string = useSelector(getLocaleState);
  const translations: Translations = useSelector(getTranslationsState);

  const tabItems: ITabItem[] = [
    {
      name: 'Rooms',
      link: 'RoomsDeals',
    },
    {
      name: 'Rating',
      link: 'GuestRating',
    },
    {
      name: 'Reviews',
      link: 'GuestReviews',
    },
    {
      name: 'Map',
      link: 'Map',
    },
    {
      name: 'Similar Hotels',
      link: 'SimilarHotels',
    },
    {
      name: 'Details',
      link: 'Details',
    },
  ];

  const [selectedTab, setSelectedTab] = useState<string>(tabItems[0].name);
  const [isStickyAnchorTabs, setIsStickyAnchorTabs] = useState<boolean>(false);
  const [isDisplayStickyAnchorTabs, setIsDisplayStickyAnchorTabs] = useState<boolean>(false);
  const [clicked, setClicked] = useState<boolean>(false);
  const anchorTabs = useRef<HTMLDivElement>(null);
  const [searchFormHeight, setSearchFormHeight] = useState<number>();

  useEffect(() => {
    const sectionIds = [
      'RoomsDeals',
      'GuestReviews',
      'Map',
      'GuestRating',
      'SimilarHotels',
      'Details',
    ];

    const searchLayoutHeight = document.getElementById('hotelSearchLayout')?.clientHeight || 0;
    setSearchFormHeight(searchLayoutHeight);

    const handleScroll = () => {
      sectionIds.forEach((id) => {
        const targetElement = document.getElementById(id);

        if (!targetElement) {
          return;
        }

        const rect = targetElement.getBoundingClientRect();

        if (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth) &&
          !clicked
        ) {
          setSelectedTab(id);
        }
      });

      const RoomsDealsCurrent = document.getElementById('RoomsDeals');
      const hotelInfoSectionCurrent = document.getElementById('HotelInfoSection');

      if (
        window.scrollY >
        (hotelInfoSectionCurrent?.offsetTop && hotelInfoSectionCurrent?.offsetTop > 0
          ? hotelInfoSectionCurrent.offsetTop + ROOMS_DEAL_MARGIN_TOP
          : 675)
      ) {
        setIsDisplayStickyAnchorTabs(true);
      } else {
        setIsDisplayStickyAnchorTabs(false);
      }

      if (
        window.scrollY + searchLayoutHeight >
        (RoomsDealsCurrent?.offsetTop && RoomsDealsCurrent?.offsetTop > 0
          ? RoomsDealsCurrent.offsetTop - ROOMS_DEAL_MARGIN_TOP
          : 675)
      ) {
        setIsStickyAnchorTabs(true);
      } else {
        setIsStickyAnchorTabs(false);
      }
    };

    const throttledHandleScroll = throttle(handleScroll, 200);

    window.addEventListener('scroll', throttledHandleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [clicked, setSelectedTab, setIsStickyAnchorTabs, anchorTabs, setIsDisplayStickyAnchorTabs]);

  const getTabItemTranslation = useCallback<(name: string) => string>(
    (name: string) => {
      switch (name) {
        case 'Rooms':
          return translateText(translations.room_count, locale, '') as string;
        case 'Rating':
          return translations.guest_rating as string;
        case 'Reviews':
          return translateText(translations.count_review, locale, '') as string;
        case 'Map':
          return translations.hotel_results_map as string;
        case 'Details':
          return translations.details as string;
        case 'Similar Hotels':
          return translations.similar_hotels_title as string;
        default:
          return '';
      }
    },
    [translations],
  );

  const scrollDownSmoothly = useCallback(
    (id: string) => {
      const targetElement = document.getElementById(id);

      if (targetElement) {
        const adjustedScrollPosition =
          targetElement.offsetTop - STICKY_BAR_HEIGHT - searchFormHeight!;

        window.scrollTo({
          top: adjustedScrollPosition,
          behavior: 'smooth',
        });
      }
    },
    [anchorTabs.current, searchFormHeight],
  );

  const handleSelectedTab = (tab: string) => {
    setClicked(true);
    setTimeout(() => {
      setClicked(false);
    }, 500);
    setSelectedTab(tab);
    scrollDownSmoothly(tab);
  };

  return (
    <>
      <div id='hotelAnchorTabs' ref={anchorTabs} className={clsx(styles.container)}>
        <div className={styles.hotelAnchorTabs}>
          {tabItems
            .filter((tab) => document.getElementById(tab.link))
            .map((tab) => (
              <div
                key={tab.name}
                className={clsx(styles.anchorTab, tab.link === selectedTab && styles.selected)}
                onClick={() => handleSelectedTab(tab.link)}
              >
                {getTabItemTranslation(tab.name)}
              </div>
            ))}
        </div>
      </div>
      {isDisplayStickyAnchorTabs && (
        <div
          ref={anchorTabs}
          className={clsx(
            styles.container,
            styles.fixed,
            styles.visible,
            !isStickyAnchorTabs && styles.hidden,
          )}
        >
          <div className={styles.hotelAnchorTabs}>
            {tabItems
              .filter((tab) => document.getElementById(tab.link))
              .map((tab) => (
                <div
                  key={tab.name}
                  className={clsx(styles.anchorTab, tab.link === selectedTab && styles.selected)}
                  onClick={() => handleSelectedTab(tab.link)}
                >
                  {getTabItemTranslation(tab.name)}
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  );
};

export default HotelAnchorTabs;
